import { Calendar, Clock, ListChecks } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Task } from "../mock/types";
import {
	allAssignees,
	checklistPct,
	formatDueDate,
	formatMinutes,
	projectsById,
	sprintsById,
	typesById,
	PRIORITY_BORDER,
} from "../mock/helpers";
import { AvatarStack } from "./AvatarStack";
import { PriorityBadge, PrBadge, TicketCode, TypeChip } from "./bits";

export function TaskCard({
	task,
	onClick,
}: {
	task: Task;
	onClick: () => void;
}) {
	const project = projectsById.get(task.project_id);
	const sprint = task.sprint_id ? sprintsById.get(task.sprint_id) : null;
	const type = task.task_type_id ? typesById.get(task.task_type_id) : null;
	const assignees = allAssignees(task);
	const total = task.checklist.length;
	const done = task.checklist.filter((c) => c.done).length;
	const pct = checklistPct(task);

	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(
				"group w-full text-left bg-surface rounded-lg border border-border border-l-[3px] p-3.5 transition-all flex flex-col gap-2.5 shadow-xs hover:shadow-md hover:-translate-y-0.5 cursor-pointer",
				PRIORITY_BORDER[task.priority],
			)}
		>
			<div className="flex items-start justify-between gap-2">
				<div className="flex items-center gap-1.5 flex-wrap">
					<PriorityBadge priority={task.priority} />
					{task.ticket_code && <TicketCode code={task.ticket_code} />}
					{task.github && (
						<PrBadge
							state={task.github.pr_state}
							prNumber={task.github.pr_number}
						/>
					)}
				</div>
				{type && <TypeChip type={type} />}
			</div>

			<div>
				<p className="text-[10px] text-muted">{project?.name ?? "—"}</p>
				<p className="text-sm font-medium text-foreground leading-snug">
					{task.title}
				</p>
			</div>

			{total > 0 && (
				<div className="flex items-center gap-1.5">
					<ListChecks className="h-3 w-3 text-muted shrink-0" />
					<div className="h-1 flex-1 rounded-full bg-muted-subtle overflow-hidden">
						<div
							className="h-full bg-primary transition-all"
							style={{ width: `${pct}%` }}
						/>
					</div>
					<span className="text-[11px] text-muted leading-none shrink-0">
						{done}/{total}
					</span>
				</div>
			)}

			<div className="flex items-center justify-between pt-2 border-t border-border">
				{assignees.length > 0 ? (
					<AvatarStack profiles={assignees} size="sm" />
				) : (
					<span className="text-[10px] text-muted">Unassigned</span>
				)}
				<div className="flex items-center gap-2 text-[11px] text-muted">
					{sprint && <span>{sprint.name}</span>}
					{task.estimated_time > 0 && (
						<span className="flex items-center gap-1">
							<Clock className="h-3 w-3" />
							{formatMinutes(task.estimated_time)}
						</span>
					)}
					{task.due_date && (
						<span className="flex items-center gap-1">
							<Calendar className="h-3 w-3" />
							{formatDueDate(task.due_date)}
						</span>
					)}
				</div>
			</div>
		</button>
	);
}
